import { createCustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { MobileUploadErrorCode } from "@redotech/customer-portal-sdk/rpc/schema/mobile-upload-enums";
import { useTranslation } from "@redotech/redo-i18n";
import CheckCircleFilledIcon from "@redotech/redo-icons/check-circle_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ClientError } from "@redotech/rpc/errors";
import { memo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as styles from "./mobile-upload-page.module.css";

const REDO_API_URL = globalThis.REDO_API_URL ?? "http://localhost:8888";
const REDO_CUSTOMER_PORTAL_SERVER_URL =
  globalThis.REDO_CUSTOMER_PORTAL_SERVER_URL ?? "http://localhost:8002";

const MAX_IMAGES = 20;

const IMAGE_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

function createPublicRpcClient(widgetId: string) {
  return createCustomerPortalRpcClient({
    baseURL: new URL(`${REDO_CUSTOMER_PORTAL_SERVER_URL}/rpc`),
    headers: { "X-WIDGET-ID": widgetId },
  });
}

async function uploadImageFile(widgetId: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("image", file);

  // Pre-existing endpoint
  const response = await fetch(`${REDO_API_URL}/widgets/${widgetId}/upload`, {
    method: "POST",
    body: form,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const data = (await response.json()) as { url: string };
  return data.url;
}

type Translate = ReturnType<typeof useTranslation>["t"];

function uploadErrorMessage(
  code: string | undefined,
  t: Translate,
  fallback: string,
): string {
  if (code === MobileUploadErrorCode.Consumed) {
    return t(
      "Your photos are already on your computer. To send more, use the Send more photos button there.",
    );
  }
  if (code === MobileUploadErrorCode.Gone) {
    return t(
      "This upload link expired. Scan the new QR code on your computer to keep going.",
    );
  }
  if (code === MobileUploadErrorCode.Limit) {
    return t("You've reached the maximum of {{count}} photos.", {
      count: MAX_IMAGES,
    });
  }
  return fallback;
}

interface UploadedImage {
  url: string;
  localPreview: string;
}

export const MobileUploadPage = memo(function MobileUploadPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const widgetId = searchParams.get("widget_id") ?? "";

  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !token || !widgetId) {
      return;
    }

    if (uploadedImages.length >= MAX_IMAGES) {
      setErrorMessage(
        t("You've reached the maximum of {{count}} photos.", {
          count: MAX_IMAGES,
        }),
      );
      return;
    }

    if (!IMAGE_TYPES.includes(file.type)) {
      setErrorMessage(
        t("Invalid file type. Accepted: jpg, jpeg, png, webp, gif"),
      );
      return;
    }

    setErrorMessage(null);
    setUploading(true);

    try {
      const localPreview = URL.createObjectURL(file);
      const imageUrl = await uploadImageFile(widgetId, file);

      const rpcClient = createPublicRpcClient(widgetId);
      await rpcClient.addMobileUploadImage({ token, imageUrl });

      setUploadedImages((prev) => [...prev, { url: imageUrl, localPreview }]);
    } catch (error) {
      const code = error instanceof ClientError ? error.code : undefined;
      // The phone's failure is otherwise invisible: it never reaches Datadog.
      console.error("Mobile upload add image failed", code);
      setErrorMessage(
        uploadErrorMessage(code, t, t("Upload failed. Please try again.")),
      );
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-selected if needed
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleDone = async () => {
    if (!token || !widgetId) {
      return;
    }
    setCompleting(true);
    try {
      const rpcClient = createPublicRpcClient(widgetId);
      await rpcClient.completeMobileUpload({ token });
      setDone(true);
    } catch (error) {
      const code = error instanceof ClientError ? error.code : undefined;
      console.error("Mobile upload complete failed", code);
      setErrorMessage(
        uploadErrorMessage(
          code,
          t,
          t("Something went wrong. Please try again."),
        ),
      );
      setCompleting(false);
    }
  };

  if (!token || !widgetId) {
    return (
      <Flex
        align="center"
        className={styles.page}
        flexDirection="column"
        gap="xl"
        justify="center"
      >
        <Text fontSize="lg" fontWeight="semibold" textAlign="center">
          {t("Invalid upload link")}
        </Text>
        <Text fontSize="sm" textAlign="center" textColor="secondary">
          {t(
            "This link is missing required parameters. Please scan the QR code again.",
          )}
        </Text>
      </Flex>
    );
  }

  if (done) {
    return (
      <Flex
        align="center"
        className={styles.page}
        flexDirection="column"
        gap="xl"
        justify="center"
      >
        <CheckCircleFilledIcon className={styles.successIcon} />
        <Text fontSize="xl" fontWeight="semibold" textAlign="center">
          {t("Photos uploaded!")}
        </Text>
        <Text fontSize="sm" textAlign="center" textColor="secondary">
          {t(
            "You can close this tab and continue your return on your computer.",
          )}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      className={styles.page}
      flexDirection="column"
      gap="2xl"
      justify="center"
    >
      <Flex flexDirection="column" gap="sm">
        <Text fontSize="xl" fontWeight="semibold" textAlign="center">
          {t("Upload photos")}
        </Text>
        <Text fontSize="sm" textAlign="center" textColor="secondary">
          {t("Take or choose photos, then tap Done when finished.")}
        </Text>
      </Flex>

      {uploadedImages.length > 0 && (
        <Flex
          className={styles.thumbnailGrid}
          flexDirection="row"
          gap="sm"
          wrap="wrap"
        >
          {uploadedImages.map((img, i) => (
            <img
              alt={t("Uploaded photo {{number}}", { number: i + 1 })}
              className={styles.thumbnail}
              key={img.url}
              src={img.localPreview}
            />
          ))}
        </Flex>
      )}

      <input
        accept={IMAGE_TYPES.join(",")}
        onChange={handleFileChange}
        ref={inputRef}
        style={{ display: "none" }}
        type="file"
      />

      <Flex flexDirection="column" gap="md" w="full">
        <RedoButton
          disabled={
            uploading || completing || uploadedImages.length >= MAX_IMAGES
          }
          hierarchy="secondary"
          onClick={() => inputRef.current?.click()}
          pending={uploading}
          text={uploading ? t("Uploading...") : t("Add a photo")}
        />

        <RedoButton
          disabled={uploadedImages.length === 0 || uploading || completing}
          hierarchy="primary"
          onClick={() => void handleDone()}
          pending={completing}
          text={t("Done")}
        />
      </Flex>

      {errorMessage && (
        <Text fontSize="sm" textAlign="center" textColor="error">
          {errorMessage}
        </Text>
      )}
    </Flex>
  );
});
